// Phase 5 commands: pickups, own-delivery runs, driver cash and empty-box transfers (04 §4.2, §4.5).
import { z } from 'zod';
import { defineCommand } from '../define';
import { branchCodeSchema, sizeCodeSchema } from '../config';
import { addressSchema, barcodeSchema, e164Schema, trackingNoSchema } from '../cargo';
import {
  geoSchema,
  pickupIntentSchema,
  pickupWantsSchema,
  pickupWindowSchema,
  STOP_FAIL_REASONS,
} from '../finalmile';
import { collectCargo } from './counter';
import { campaignCodeSchema } from '../customerConfig';

const minor = z.number().int().nonnegative().max(1e12);
const id = z.string().min(1).max(40);
const at = z.iso.datetime({ offset: true });
const path = z.string().max(200);
const barcodes = z.array(barcodeSchema).min(1).max(200);
const ulidId = (prefix: string) =>
  z.string().regex(new RegExp(`^${prefix}_[0-9A-HJKMNP-TV-Z]{26}$`));

// ---- Pickups -------------------------------------------------------------------------

export const requestPickup = defineCommand({
  type: 'requestPickup',
  permission: 'pickup.manage',
  principals: ['customer', 'website'],
  offline: false,
  payload: z
    .object({
      pickupRequestId: ulidId('pur'),
      customerId: id.optional(),
      contact: z
        .object({ name: z.string().trim().min(1).max(120), phoneE164: e164Schema })
        .strict(),
      address: addressSchema,
      geo: geoSchema.optional(),
      window: pickupWindowSchema,
      wants: pickupWantsSchema,
      /** What the customer wants to ship, from the app (kept for the dispatcher and the driver). */
      intent: pickupIntentSchema.optional(),
      notes: z.string().max(500).default(''),
      source: z.enum(['office', 'phone', 'app', 'whatsapp', 'website']).default('office'),
      /** The campaign the booking came through (15 §15.2); the first shipment copies it to the customer. */
      campaignCode: campaignCodeSchema.optional(),
    })
    .strict(),
});
export const schedulePickup = defineCommand({
  type: 'schedulePickup',
  permission: 'pickup.manage',
  principals: [],
  offline: true,
  payload: z
    .object({ pickupRequestId: id, window: pickupWindowSchema, runId: id.optional() })
    .strict(),
});
export const cancelPickup = defineCommand({
  type: 'cancelPickup',
  permission: 'pickup.manage',
  principals: ['customer'],
  offline: true,
  payload: z.object({ pickupRequestId: id, reason: z.string().trim().min(3).max(300) }).strict(),
});
export const completePickup = defineCommand({
  type: 'completePickup',
  permission: 'pickup.complete',
  principals: [],
  offline: true,
  payload: z
    .object({
      runId: id,
      stopId: id,
      emptyBoxesDropped: z
        .array(
          z
            .object({
              sizeId: sizeCodeSchema,
              qty: z.number().int().positive().max(50),
              barcodes: z.array(barcodeSchema).max(50).default([]),
            })
            .strict(),
        )
        .max(10)
        .default([]),
      /** The cargo taken, exactly as the counter's collectCargo; null when only empty boxes were dropped. */
      cargo: collectCargo.payload.nullable(),
      /** Cash the driver took for empty boxes sold at the door (cargo payments travel inside `cargo`). */
      emptyBoxCashMinor: minor.default(0),
      photos: z.array(path).max(10).default([]),
      signaturePath: path.default(''),
      geo: geoSchema.optional(),
    })
    .strict(),
});
export const failPickup = defineCommand({
  type: 'failPickup',
  permission: 'pickup.complete',
  alsoPermitted: ['finalmile.plan'],
  principals: [],
  offline: true,
  payload: z
    .object({
      runId: id,
      stopId: id,
      reason: z.enum(STOP_FAIL_REASONS),
      note: z.string().max(500).default(''),
      photos: z.array(path).max(5).default([]),
      geo: geoSchema.optional(),
    })
    .strict(),
});

// ---- Runs --------------------------------------------------------------------------------

export const planRun = defineCommand({
  type: 'planRun',
  permission: 'finalmile.plan',
  principals: [],
  offline: false,
  payload: z
    .object({
      runId: ulidId('run'),
      type: z.enum(['pickup', 'delivery', 'mixed']),
      date: z.iso.date(),
      zoneIds: z.array(z.string().max(60)).max(20).default([]),
      /** Stops in the planner's order: shipments to deliver and/or pickup requests. */
      stops: z
        .array(
          z.union([
            z.object({ kind: z.literal('delivery'), shipmentId: trackingNoSchema }).strict(),
            z.object({ kind: z.literal('pickup'), pickupRequestId: id }).strict(),
          ]),
        )
        .min(1)
        .max(120),
    })
    .strict(),
});
export const updateRunStops = defineCommand({
  type: 'updateRunStops',
  permission: 'finalmile.plan',
  principals: [],
  offline: false,
  payload: z
    .object({
      runId: id,
      add: z
        .array(
          z.union([
            z.object({ kind: z.literal('delivery'), shipmentId: trackingNoSchema }).strict(),
            z.object({ kind: z.literal('pickup'), pickupRequestId: id }).strict(),
          ]),
        )
        .max(120)
        .default([]),
      removeStopIds: z.array(id).max(120).default([]),
      order: z.array(id).max(240).optional(),
    })
    .strict(),
});
export const assignRun = defineCommand({
  type: 'assignRun',
  permission: 'finalmile.plan',
  principals: [],
  offline: true,
  payload: z
    .object({ runId: id, driverUid: z.string().min(1).max(128), vehicleId: id.optional() })
    .strict(),
});
export const startRun = defineCommand({
  type: 'startRun',
  permission: 'finalmile.deliver',
  alsoPermitted: ['pickup.complete'],
  principals: [],
  offline: true,
  /** The loading scan: boxes on the van. Pickup-only runs send an empty list. */
  payload: z
    .object({
      runId: id,
      barcodes: z.array(barcodeSchema).max(200).default([]),
      geo: geoSchema.optional(),
    })
    .strict(),
});
export const arriveStop = defineCommand({
  type: 'arriveStop',
  permission: 'finalmile.deliver',
  alsoPermitted: ['pickup.complete'],
  principals: [],
  offline: true,
  payload: z
    .object({
      runId: id,
      stopId: id,
      at,
      geo: geoSchema.optional(),
      notifyCustomer: z.boolean().default(false),
    })
    .strict(),
});
export const deliverStop = defineCommand({
  type: 'deliverStop',
  permission: 'finalmile.deliver',
  principals: [],
  offline: true,
  payload: z
    .object({
      runId: id,
      stopId: id,
      barcodes,
      receiverName: z.string().trim().min(1).max(80),
      relationship: z.string().max(40).default(''),
      deliveryCode: z
        .string()
        .regex(/^\d{4}$/)
        .optional(),
      idPhotoPath: path.optional(),
      photos: z.array(path).max(10).default([]),
      signaturePath: path.default(''),
      geo: geoSchema.optional(),
      collectedMinor: minor.default(0),
      collectedMethod: z.enum(['cash', 'transfer']).default('cash'),
      /** Office staff recording for a driver whose phone died. */
      onBehalfOfUid: z.string().max(128).optional(),
    })
    .strict(),
});
export const failStop = defineCommand({
  type: 'failStop',
  permission: 'finalmile.deliver',
  principals: [],
  offline: true,
  payload: z
    .object({
      runId: id,
      stopId: id,
      reason: z.enum(STOP_FAIL_REASONS),
      note: z.string().max(500).default(''),
      photos: z.array(path).max(5).default([]),
      geo: geoSchema.optional(),
    })
    .strict(),
});
export const closeRun = defineCommand({
  type: 'closeRun',
  permission: 'finalmile.deliver',
  alsoPermitted: ['pickup.complete', 'finalmile.plan'],
  principals: [],
  offline: true,
  /** The scan-back: every undelivered box that came back to the branch. */
  payload: z
    .object({
      runId: id,
      barcodesBack: z.array(barcodeSchema).max(400).default([]),
      note: z.string().max(300).default(''),
    })
    .strict(),
});
export const handoverDriverCash = defineCommand({
  type: 'handoverDriverCash',
  permission: 'finance.cash.receive',
  principals: [],
  offline: true,
  payload: z
    .object({
      driverUid: z.string().min(1).max(128),
      countedMinor: minor,
      runIds: z.array(id).max(20).default([]),
      note: z.string().max(300).default(''),
    })
    .strict(),
});

// ---- Empty boxes between branches --------------------------------------------------------

export const transferEmptyBoxes = defineCommand({
  type: 'transferEmptyBoxes',
  permission: 'stock.manage',
  principals: [],
  offline: true,
  payload: z
    .object({
      transferId: ulidId('xfr'),
      toBranchId: branchCodeSchema,
      items: z
        .array(
          z.object({ sizeId: sizeCodeSchema, qty: z.number().int().positive().max(5000) }).strict(),
        )
        .min(1)
        .max(10),
      note: z.string().max(300).default(''),
    })
    .strict(),
});
export const receiveEmptyBoxTransfer = defineCommand({
  type: 'receiveEmptyBoxTransfer',
  permission: 'stock.manage',
  principals: [],
  offline: true,
  payload: z
    .object({
      transferId: id,
      items: z
        .array(
          z
            .object({ sizeId: sizeCodeSchema, qty: z.number().int().nonnegative().max(5000) })
            .strict(),
        )
        .min(1)
        .max(10),
      note: z.string().max(300).default(''),
    })
    .strict(),
});

export const FINALMILE_COMMANDS = {
  requestPickup,
  schedulePickup,
  cancelPickup,
  completePickup,
  failPickup,
  planRun,
  updateRunStops,
  assignRun,
  startRun,
  arriveStop,
  deliverStop,
  failStop,
  closeRun,
  handoverDriverCash,
  transferEmptyBoxes,
  receiveEmptyBoxTransfer,
} as const;
